// src/encounter/lib/patient-identity.ts
//
// PROD-2743 · frame v2 only. Design desk D-68 (6 Oct, audit FV2-39): the compact patient identity line's
// parts, and whether the chart card is hidden. The line itself is frame-v2-patient-identity.tsx.
import { createContext } from 'react';
import type { PatientHeaderData, PatientHeaderDemographics } from '@scripsteam/scrips-signal-ds';

/** True while the chart column's patient card is NOT on screen (frame v2). Default false: outside
 *  the frame nothing is hidden, so the line never draws. */
export const ChartCardHiddenContext = createContext(false);

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * The date of birth in the D-45 date format ("15 Mar 1980"). A calendar date is read from its own
 * digits (a birth date is not an instant: parsing "1980-03-15" as UTC midnight would move it a day
 * in any zone behind UTC). A value the page already formatted ("15 Mar 1980", `to-patient-header`)
 * is kept as it is; nothing usable → undefined.
 */
export function identityDob(dob: string | null | undefined): string | undefined {
  const s = dob?.trim();
  if (!s) return undefined;
  const m = ISO_DATE.exec(s);
  if (!m) return s;
  const [, y, mo, d] = m;
  const month = Number(mo);
  const day = Number(d);
  // Never normalise an impossible date into a different, valid-looking one (Codex P2: "2026-02-30" read as
  // 2 Mar): a calendar date that does not exist is shown exactly as recorded. The year is kept literally.
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(Number(y), month)) return s;
  return `${day} ${D45_MONTHS[month - 1]} ${y}`;
}

/** The D-45 short months (date-format.ts `formatDateD45`). */
const D45_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function daysInMonth(year: number, month: number): number {
  const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  return [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1];
}

/** The header's age, the prototype's words (Antonin, 19 Oct): "26y" reads "26 years" ("1 year"). The backend's own display age for a
 *  child ("1y 5m 24d") is kept as it is; nothing usable → undefined. */
export function ageInWords(age: string | null | undefined): string | undefined {
  const a = age?.trim();
  if (!a) return undefined;
  const m = /^(\d+)y$/i.exec(a);
  return m ? `${m[1]} ${Number(m[1]) === 1 ? 'year' : 'years'}` : a;
}

export interface PatientIdentityParts {
  name: string;
  /** "46y", or the backend's own display age ("1y 5m 24d") when it gave one. */
  age?: string;
  /** "DOB 15 Mar 1980". */
  dob?: string;
  /** "MRN 00123" — as the chart card draws it. */
  mrn?: string;
}

/** The compact identity line's parts. Each identifier appears only when the record has it. */
export function patientIdentityParts(
  patient: PatientHeaderData,
  demographics: PatientHeaderDemographics | undefined,
): PatientIdentityParts {
  const age = demographics?.ageDisplay?.trim() || (demographics?.age != null ? `${demographics.age}y` : undefined);
  const dob = identityDob(demographics?.dob);
  const mrn = demographics?.mrn?.trim();
  return {
    name: patient.name,
    ...(age ? { age } : {}),
    ...(dob ? { dob: `DOB ${dob}` } : {}),
    ...(mrn ? { mrn: `MRN ${mrn}` } : {}),
  };
}

